import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FaImage, FaSave, FaTimes, FaUpload } from "react-icons/fa";

import {
  buildInitialUserForm,
  getMediaUrl,
  normalizeUserPayload,
  USER_ROLES,
  validateUserForm,
} from "../utils/leClubUtils";

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-orange-400 focus:ring-4 focus:ring-orange-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-orange-500/10";

const labelClass =
  "mb-1.5 block text-sm font-bold text-slate-700 dark:text-slate-200";

function TextInput({
  label,
  name,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder = "",
}) {
  return (
    <div>
      <label className={labelClass}>
        {label}
        {required && <span className="text-rose-500"> *</span>}
      </label>

      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className={inputClass}
      />
    </div>
  );
}

export default function UserForm({
  mode = "create",
  user,
  saving = false,
  onSubmit,
  onClose,
}) {
  const initialForm = useMemo(() => buildInitialUserForm(user), [user]);

  const [formData, setFormData] = useState(initialForm);
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    setFormData(initialForm);
    setPreviewUrl(getMediaUrl(initialForm.photoURL));
  }, [initialForm]);

  useEffect(() => {
    if (!formData.imageFile) return undefined;

    const objectUrl = URL.createObjectURL(formData.imageFile);
    setPreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [formData.imageFile]);

  const handleChange = (event) => {
    const { name, value, files } = event.target;

    if (name === "imageFile") {
      setFormData((prev) => ({
        ...prev,
        imageFile: files?.[0] || null,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "photoURL") {
      setPreviewUrl(getMediaUrl(value));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const payload = normalizeUserPayload(formData, mode);
    const errorMessage = validateUserForm(payload, mode);

    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    await onSubmit({
      ...payload,
      imageFile: formData.imageFile,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <section className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
        <h3 className="text-base font-bold text-slate-950 dark:text-white">
          Informations utilisateur
        </h3>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <TextInput
            label="Nom"
            name="nom"
            value={formData.nom}
            onChange={handleChange}
            required
            placeholder="Nom"
          />

          <TextInput
            label="Prénom"
            name="prenom"
            value={formData.prenom}
            onChange={handleChange}
            required
            placeholder="Prénom"
          />

          <div className="sm:col-span-2">
            <TextInput
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="email@club.fr"
            />
          </div>

          <TextInput
            label={
              mode === "create"
                ? "Mot de passe"
                : "Nouveau mot de passe"
            }
            name="password"
            type="text"
            value={formData.password}
            onChange={handleChange}
            required={mode === "create"}
            placeholder={
              mode === "create"
                ? "Mot de passe obligatoire"
                : "Laisser vide si inchangé"
            }
          />

          <div>
            <label className={labelClass}>
              Rôle <span className="text-rose-500">*</span>
            </label>
            <select
              name="role_id"
              value={formData.role_id}
              onChange={handleChange}
              className={inputClass}
            >
              <option value="">Choisissez un rôle</option>
              {USER_ROLES.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
        <h3 className="text-base font-bold text-slate-950 dark:text-white">
          Photo utilisateur
        </h3>

        <div className="mt-4 grid gap-5 lg:grid-cols-[260px_minmax(0,1fr)]">
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950">
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Aperçu utilisateur"
                className="h-64 w-full object-cover"
              />
            ) : (
              <div className="flex h-64 flex-col items-center justify-center text-slate-400">
                <FaImage className="text-4xl" />
                <p className="mt-3 text-sm font-medium">Aucune photo</p>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <label className={labelClass}>Télécharger une photo</label>

              <label className="flex cursor-pointer flex-col items-center justify-center rounded-3xl border border-dashed border-slate-300 bg-white px-4 py-6 text-center transition hover:border-orange-300 hover:bg-orange-50/50 dark:border-slate-700 dark:bg-slate-950 dark:hover:border-orange-500/40 dark:hover:bg-orange-500/10">
                <FaUpload className="text-xl text-orange-500" />
                <span className="mt-2 text-sm font-bold text-slate-700 dark:text-slate-200">
                  Choisir un fichier
                </span>
                <span className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Photo de profil recommandée
                </span>

                <input
                  type="file"
                  name="imageFile"
                  accept="image/*"
                  onChange={handleChange}
                  className="sr-only"
                />
              </label>
            </div>

            <TextInput
              label="Ou URL / chemin de la photo"
              name="photoURL"
              value={formData.photoURL}
              onChange={handleChange}
              placeholder="/uploads/users/photo.jpg ou https://..."
            />
          </div>
        </div>
      </section>

      <div className="sticky bottom-0 -mx-5 border-t border-slate-200 bg-white/90 px-5 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-slate-950/20 transition hover:-translate-y-0.5 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
          >
            <FaSave />
            {saving
              ? "Enregistrement..."
              : mode === "create"
              ? "Créer l'utilisateur"
              : "Enregistrer"}
          </button>
        </div>
      </div>
    </form>
  );
}